/* Tela dedicada "Como Funciona" do Portal do Publisher (ADR-0724 + ADR-0728). O conteúdo do guia
   editorial NÃO é hardcoded: vive como tópicos de FAQ na categoria estável `publisher-guide`, lidos
   pelo app no portal do publisher (`publisher-guide-section.tsx`). Esta tela é a superfície de gestão
   PRÓPRIA desse guia — antes o staff só conseguia editá-lo pela tela geral de FAQ digitando a
   categoria certa (parecia hardcoded). Reusa a infra de FAQ (hooks/tabela/modal) filtrando
   SERVER-SIDE por `categoryKey=publisher-guide` (ADR-0728) e criando tópicos com essa categoria
   pré-fixada e read-only. */
import type { FaqStatus, FaqTopic, StaffRole } from '@planuze/admin-contracts';
import { FAQ_STATUSES } from '@planuze/admin-contracts';
import {
  Button,
  ConfirmDialog,
  InlineAlert,
  Page,
  PageActions,
  PageContent,
  PageDescription,
  PageHeader,
  PageTitle,
  PageTitleBlock,
  TableSkeleton,
  toast,
} from '@planuze/ui';
import { useMemo, useState } from 'react';
import { FaqFormModal } from '../components/faq/faq-form-modal.js';
import { FaqTable } from '../components/faq/faq-table.js';
import { SegmentedControl, type SegmentOption } from '../components/filters/segmented-control.js';
import { SearchInput } from '../components/search-input.js';
import { usePublisherGuideContent } from '../content/sections/publisher-guide.js';
import { useDeleteFaqTopic, useFaqTopics } from '../hooks/use-faq.js';
import { useStaffSession } from '../hooks/use-staff-session.js';

/* Categoria estável que alimenta a seção "Como Funciona" do portal do publisher (ADR-0724). */
const PUBLISHER_GUIDE_CATEGORY = 'publisher-guide';
const MANAGE_ROLES: readonly StaffRole[] = ['super_admin', 'support'];
type FaqStatusFilter = 'all' | FaqStatus;

function topicHasStatus(topic: FaqTopic, status: FaqStatus): boolean {
  return topic.locales.some((l) => l.status === status);
}

export function PublisherGuidePage() {
  const copy = usePublisherGuideContent();
  const session = useStaffSession();
  const canManage = session !== null && MANAGE_ROLES.includes(session.role);
  const { data, isLoading, isError, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useFaqTopics(PUBLISHER_GUIDE_CATEGORY);
  const del = useDeleteFaqTopic();
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<FaqTopic | undefined>(undefined);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<FaqStatusFilter>('all');
  const [toDelete, setToDelete] = useState<FaqTopic | null>(null);

  const openCreate = (): void => {
    setEditing(undefined);
    setModalOpen(true);
  };
  const openEdit = (topic: FaqTopic): void => {
    setEditing(topic);
    setModalOpen(true);
  };

  const topics = useMemo(() => data?.pages.flatMap((page) => page.items) ?? [], [data]);

  const statusCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const s of FAQ_STATUSES) counts[s] = topics.filter((t) => topicHasStatus(t, s)).length;
    return counts;
  }, [topics]);
  const statusOptions: readonly SegmentOption<FaqStatusFilter>[] = [
    { value: 'all', label: copy.allStatusesLabel, count: topics.length },
    ...FAQ_STATUSES.map((s) => ({
      value: s,
      label: copy.statusLabels[s],
      count: statusCounts[s] ?? 0,
    })),
  ];

  const query = search.trim().toLowerCase();
  const filtered = useMemo(
    () =>
      topics.filter((topic) => {
        if (statusFilter !== 'all' && !topicHasStatus(topic, statusFilter)) return false;
        if (
          query.length > 0 &&
          !topic.slug.toLowerCase().includes(query) &&
          !topic.locales.some(
            (l) =>
              l.question.toLowerCase().includes(query) ||
              l.categoryLabel.toLowerCase().includes(query),
          )
        ) {
          return false;
        }
        return true;
      }),
    [topics, statusFilter, query],
  );

  const hasFilters = query.length > 0 || statusFilter !== 'all';
  const clearFilters = (): void => {
    setSearch('');
    setStatusFilter('all');
  };

  return (
    <Page>
      <PageHeader>
        <PageTitleBlock>
          <PageTitle>{copy.pageTitle}</PageTitle>
          <PageDescription>{copy.pageDescription}</PageDescription>
        </PageTitleBlock>
        {canManage && (
          <PageActions>
            <Button onClick={openCreate}>{copy.newTopicButton}</Button>
          </PageActions>
        )}
      </PageHeader>
      <PageContent className="space-y-4">
        <InlineAlert variant="info">
          {copy.infoIntro}
          <strong>{copy.howItWorksLabel}</strong>
          {copy.infoMiddle}
          <code>publisher-guide</code>
          {copy.infoAfterCategory}
          <strong>{copy.publishedWord}</strong>
          {copy.infoAfterPublished}
        </InlineAlert>
        <div className="flex flex-wrap items-center gap-3">
          <SearchInput
            value={search}
            onChange={setSearch}
            ariaLabel={copy.searchAriaLabel}
            placeholder={copy.searchPlaceholder}
          />
          <SegmentedControl
            value={statusFilter}
            onChange={setStatusFilter}
            options={statusOptions}
            ariaLabel={copy.statusFilterAriaLabel}
          />
        </div>
        {isError ? (
          <InlineAlert variant="destructive">{copy.loadError}</InlineAlert>
        ) : isLoading ? (
          <TableSkeleton columns={canManage ? 5 : 4} />
        ) : (
          <FaqTable
            items={filtered}
            canManage={canManage}
            onEdit={openEdit}
            onDelete={setToDelete}
            deleting={del.isPending}
            onCreate={openCreate}
            hasFilters={hasFilters}
            onClearFilters={clearFilters}
          />
        )}
        {hasNextPage && (
          <div className="flex justify-center">
            <Button
              variant="ghost"
              loading={isFetchingNextPage}
              onClick={() => {
                fetchNextPage().catch(() => undefined);
              }}
            >
              {copy.loadMoreButton}
            </Button>
          </div>
        )}
      </PageContent>
      {modalOpen && (
        <FaqFormModal
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          topic={editing}
          fixedCategoryKey={PUBLISHER_GUIDE_CATEGORY}
        />
      )}
      <ConfirmDialog
        open={toDelete !== null}
        onOpenChange={(open) => {
          if (!open) setToDelete(null);
        }}
        title={copy.deleteDialogTitle}
        description={toDelete !== null ? copy.deleteDialogDescription(toDelete.slug) : undefined}
        confirmLabel={copy.deleteConfirmButton}
        variant="destructive"
        onConfirm={async () => {
          const topic = toDelete;
          if (topic === null) return;
          try {
            await del.mutateAsync({ slug: topic.slug, ids: topic.locales.map((l) => l.id) });
            toast.success(copy.deleteSuccessToast);
          } catch {
            toast.error(copy.deleteErrorToast);
          }
          setToDelete(null);
        }}
      />
    </Page>
  );
}
